import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Dimensions, ScrollView, Alert, useWindowDimensions } from 'react-native';
import { Surface, Appbar, TextInput, Button, Text, useTheme, Snackbar, Menu } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { Sidebar, SIDEBAR_WIDTH } from '../../src/components/Sidebar';
import { useUser } from '../../src/hooks/useUser';
import { useProfile } from '../../src/hooks/useProfile';
import { getMyCompany, updateMyCompany, logout } from '../../src/api/apiService';
import { useRouter } from 'expo-router';
import { CountryCodeSelector, COUNTRY_CODES } from '../../src/components/CountryCodeSelector';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

export default function CompanyScreen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { width } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  const { handleLogout, userData } = useUser();
  const router = useRouter();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Use the new profile hook
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [nipc, setNipc] = useState('');
  const [address, setAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+351');
  const [menuVisible, setMenuVisible] = useState(false);
  const [licenseTypeMenuVisible, setLicenseTypeMenuVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [certidaoPermanente, setCertidaoPermanente] = useState('');
  const [tipoLicencaOperacao, setTipoLicencaOperacao] = useState('');
  const [numeroLicenca, setNumeroLicenca] = useState('');
  const [initialEmail, setInitialEmail] = useState('');
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    address?: string;
    phone_number?: string;
    email?: string;
    certidao_permanente?: string;
    tipo_licenca_operacao?: string;
    numero_licenca?: string;
    non_field_errors?: string[];
    detail?: string;
    other?: string[];
  }>({});
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // License type options matching backend choices
  const licenseTypeOptions = [
    { label: t('licenseType.selectType', 'Selecionar tipo...'), value: '' },
    { label: t('licenseType.tvde', 'Operador TVDE'), value: 'Operador TVDE' },
    { label: t('licenseType.taxi', 'Taxi'), value: 'Taxi' },
    { label: t('licenseType.lightGoods', 'Veículos ligeiros de mercadorias'), value: 'Veículos ligeiros de mercadorias' },
    { label: t('licenseType.roadTransport', 'Transporte Rodoviário (Sem Tacógrafo)'), value: 'Transporte Rodoviário (Sem Tacógrafo)' },
    { label: t('licenseType.other', 'Outro'), value: 'Outro' },
  ];

  // Mask function for license number (format: NNNNNNNNNN/AAAA)
  const applyMaskLicense = (value: string): string => {
    const cleaned = value.replace(/\D/g, '').slice(0, 14); // max 10 + 4
    if (cleaned.length <= 4) {
      return cleaned;
    }

    const yearPart = cleaned.slice(-4);
    const numberPart = cleaned.slice(0, -4).slice(0, 10);
    return `${numberPart}/${yearPart}`;
  };

  // Mask function for certidao permanente (12 digits formatted as XXXX-XXXX-XXXX)
  const applyMaskCertidao = (value: string): string =>
    value
      .replace(/\D/g, '')
      .replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')
      .substring(0, 14);

  useEffect(() => {
    const fetchCompany = async () => {
      setLoading(true);
      const res = await getMyCompany();
      if (res.ok && res.data) {
        setName(res.data.name || '');
        setNipc(res.data.nipc || '');
        setAddress(res.data.address || '');
        const phone = res.data.phone_number || '';
        if (phone && phone.startsWith('+')) {
          // Robust parsing using known country codes list; pick the longest matching code prefix
          const candidate = COUNTRY_CODES
            .filter(code => phone.startsWith(code))
            .sort((a, b) => b.length - a.length)[0];
          if (candidate) {
            setCountryCode(candidate);
            setPhoneNumber(phone.slice(candidate.length));
          } else {
            // Fallback to entire string after '+' as local part
            const m = phone.match(/^(\+\d{1,4})(.*)$/);
            setCountryCode(m ? m[1] : '+');
            setPhoneNumber(m ? m[2] : phone.replace('+', ''));
          }
        } else {
          setPhoneNumber(phone);
        }
        setEmail(res.data.email || '');
        setInitialEmail(res.data.email || '');
        
        // Format certidao_permanente with dashes
        const certidao = res.data.certidao_permanente || '';
        setCertidaoPermanente(certidao ? applyMaskCertidao(certidao) : '');
        
        setTipoLicencaOperacao(res.data.tipo_licenca_operacao || '');
        setNumeroLicenca(res.data.numero_licenca || '');
      } else {
        Alert.alert(t('error', 'Error'), t('failedToLoadCompany', 'Failed to load company info'));
      }
      setLoading(false);
    };
    fetchCompany();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      // Clear previous errors
      setFormErrors({});
      
      // Validate license number format if provided (NNNNNNNNNN/AAAA)
      if (numeroLicenca) {
        const licenseRegex = /^\d{1,10}\/\d{4}$/;
        if (!licenseRegex.test(numeroLicenca)) {
          setFormErrors({ numero_licenca: t('companyInfo.invalidLicenseFormat', 'O número da licença deve estar no formato NNNNNNNNNN/AAAA (ex: 1234567890/2024)') });
          setSaving(false);
          return;
        }
      }
      
      const normalizedEmail = (email || '').trim();
      const normalizedInitial = (initialEmail || '').trim();
      const payload: any = {
        name: name || null,
        address: address || null,
        phone_number: phoneNumber ? `${countryCode}${phoneNumber}` : null,
        certidao_permanente: certidaoPermanente ? certidaoPermanente.replace(/\D/g, '') : null,
        tipo_licenca_operacao: tipoLicencaOperacao || null,
        numero_licenca: numeroLicenca || null,
        email: normalizedEmail || null,
      };
      const res = await updateMyCompany(payload);
      if (res.ok) {
        if (normalizedEmail.toLowerCase() !== normalizedInitial.toLowerCase()) {
          setSnackbarMessage(t('userData.emailChangeSuccess', 'Email updated. Please check your new email to verify. You will be logged out.'));
          setSnackbarVisible(true);
          setInitialEmail(normalizedEmail);
          setTimeout(async () => {
            await logout();
            router.replace('/');
          }, 3000);
        } else {
          setSnackbarMessage(t('companyUpdated', 'Company details updated successfully'));
          setSnackbarVisible(true);
        }
      } else {
        const data: any = res.data || {};
        const newErrors: any = {};
        const setFirst = (arr: any) => Array.isArray(arr) ? String(arr[0]) : String(arr);
        if (data) {
          // Map known fields
          if (data.name) newErrors.name = setFirst(data.name);
          if (data.address) newErrors.address = setFirst(data.address);
          if (data.phone_number) newErrors.phone_number = setFirst(data.phone_number);
          if (data.email) newErrors.email = setFirst(data.email);
          if (data.certidao_permanente) newErrors.certidao_permanente = setFirst(data.certidao_permanente);
          if (data.tipo_licenca_operacao) newErrors.tipo_licenca_operacao = setFirst(data.tipo_licenca_operacao);
          if (data.numero_licenca) newErrors.numero_licenca = setFirst(data.numero_licenca);
          if (data.non_field_errors) newErrors.non_field_errors = Array.isArray(data.non_field_errors) ? data.non_field_errors.map(String) : [String(data.non_field_errors)];
          if (data.detail) newErrors.detail = String(data.detail);
          // Capture any other keys as generic messages
          const known = new Set(['name','address','phone_number','email','certidao_permanente','tipo_licenca_operacao','numero_licenca','non_field_errors','detail']);
          const others: string[] = [];
          Object.keys(data).forEach(k => {
            if (!known.has(k)) {
              const v = data[k];
              if (Array.isArray(v)) others.push(...v.map(String)); else if (v) others.push(String(v));
            }
          });
          if (others.length) newErrors.other = others;
        }
        setFormErrors(newErrors);
        // Show a compact alert for any general error (detail/non-field/other)
        const generalMsg = newErrors.detail || (newErrors.non_field_errors && newErrors.non_field_errors.join('\n')) || (newErrors.other && newErrors.other.join('\n'));
        if (generalMsg) {
          Alert.alert(t('error', 'Error'), generalMsg);
        }
      }
    } catch (e) {
      Alert.alert(t('error', 'Error'), t('failedToUpdateCompany', 'Failed to update company'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.container}>
      {isLargeScreen && (
        <Sidebar
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={hasOwner}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasDriver && hasOwner ? (newProfile) => updateProfile(newProfile) : undefined}
          onRegisterDriver={hasOwner && !hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
      <Surface style={[{ flex: 1, backgroundColor: theme.colors.background }, isLargeScreen && { marginLeft: SIDEBAR_WIDTH }]}>
        <Appbar.Header>
          {!isLargeScreen && <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} />}
          <Appbar.Content title={t('companyInfo.title', 'Company')} />
        </Appbar.Header>
        <ScrollView contentContainerStyle={styles.content}>
          {loading ? (
            <Text>{t('loading', 'Loading...')}</Text>
          ) : (
            <View style={{ gap: 12 }}>
              <TextInput 
                label={t('companyName', 'Company Name')} 
                value={name} 
                onChangeText={(v) => { 
                  setName(v); 
                  if (formErrors.name) setFormErrors(prev => ({ ...prev, name: undefined })); 
                }} 
                mode="outlined" 
                error={!!formErrors.name}
              />
              {formErrors.name && (
                <Text style={{ color: theme.colors.error, marginTop: -8 }}>{formErrors.name}</Text>
              )}
              <TextInput label={t('nipc', 'NIPC')} value={nipc} disabled mode="outlined" />
              <TextInput label={t('address', 'Address')} value={address} onChangeText={(v) => { setAddress(v); if (formErrors.address) setFormErrors(prev => ({ ...prev, address: undefined })); }} mode="outlined" error={!!formErrors.address} />
              {formErrors.address && (
                <Text style={{ color: theme.colors.error, marginTop: -8 }}>{formErrors.address}</Text>
              )}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Button
                  mode="outlined"
                  onPress={() => setMenuVisible(true)}
                  style={{ minWidth: 90 }}
                >
                  {countryCode}
                </Button>
                <TextInput
                  label={t('phoneNumber', 'Phone Number')}
                  value={phoneNumber}
                  onChangeText={(v) => { setPhoneNumber(v); if (formErrors.phone_number) setFormErrors(prev => ({ ...prev, phone_number: undefined })); }}
                  keyboardType="phone-pad"
                  mode="outlined"
                  style={{ flex: 1 }}
                  maxLength={15}
                  error={!!formErrors.phone_number}
                />
              </View>
              {formErrors.phone_number && (
                <Text style={{ color: theme.colors.error, marginTop: -8 }}>{formErrors.phone_number}</Text>
              )}
              <CountryCodeSelector
                visible={menuVisible}
                onDismiss={() => setMenuVisible(false)}
                onSelect={setCountryCode}
                selectedCode={countryCode}
              />
              <TextInput 
                label={t('emailAddress', 'Email Address')} 
                value={email} 
                onChangeText={(v) => { setEmail(v); if (formErrors.email) setFormErrors(prev => ({ ...prev, email: undefined })); }} 
                keyboardType="email-address" 
                autoCapitalize="none" 
                mode="outlined" 
                error={!!formErrors.email}
                disabled={true}
                right={<TextInput.Icon icon="information-outline" />}
              />
              {formErrors.email && (
                <Text style={{ color: theme.colors.error, marginTop: -8 }}>{formErrors.email}</Text>
              )}
              <Text style={{ fontSize: 12, marginTop: -8, color: theme.colors.onSurfaceVariant }}>
                {t('companyInfo.emailNote', 'O email da empresa sincroniza automaticamente com o email do proprietário')}
              </Text>
              
              <TextInput 
                label={t('permanentCertificate', 'Certidão Permanente')} 
                value={certidaoPermanente} 
                onChangeText={(v) => { 
                  setCertidaoPermanente(applyMaskCertidao(v)); 
                  if (formErrors.certidao_permanente) setFormErrors(prev => ({ ...prev, certidao_permanente: undefined })); 
                }} 
                keyboardType="number-pad" 
                mode="outlined" 
                error={!!formErrors.certidao_permanente}
                placeholder="XXXX-XXXX-XXXX"
                maxLength={14}
              />
              {formErrors.certidao_permanente && (
                <Text style={{ color: theme.colors.error, marginTop: -8 }}>{formErrors.certidao_permanente}</Text>
              )}
              
              <Menu
                visible={licenseTypeMenuVisible}
                onDismiss={() => setLicenseTypeMenuVisible(false)}
                anchor={
                  <TextInput
                    label={t('companyInfo.licenseType', 'Tipo de Licença de Operação')}
                    value={tipoLicencaOperacao}
                    mode="outlined"
                    editable={false}
                    error={!!formErrors.tipo_licenca_operacao}
                    right={
                      <TextInput.Icon
                        icon="chevron-down"
                        onPress={() => setLicenseTypeMenuVisible(true)}
                      />
                    }
                    onPressIn={() => setLicenseTypeMenuVisible(true)}
                    placeholder={t('licenseType.selectType', 'Selecionar tipo...')}
                  />
                }
                contentStyle={{ marginTop: 8, maxWidth: 400 }}
              >
                <ScrollView style={{ maxHeight: 300 }}>
                  {licenseTypeOptions.map((option) => (
                    <Menu.Item
                      key={option.value}
                      onPress={() => {
                        setTipoLicencaOperacao(option.value);
                        setLicenseTypeMenuVisible(false);
                        if (formErrors.tipo_licenca_operacao) setFormErrors(prev => ({ ...prev, tipo_licenca_operacao: undefined }));
                      }}
                      title={option.label}
                      style={
                        tipoLicencaOperacao === option.value
                          ? { backgroundColor: theme.colors.primaryContainer }
                          : undefined
                      }
                    />
                  ))}
                </ScrollView>
              </Menu>
              {formErrors.tipo_licenca_operacao && (
                <Text style={{ color: theme.colors.error, marginTop: -8 }}>{formErrors.tipo_licenca_operacao}</Text>
              )}
              
              <TextInput 
                label={t('companyInfo.licenseNumber', 'Número da Licença')} 
                value={numeroLicenca} 
                onChangeText={(v) => { 
                  setNumeroLicenca(applyMaskLicense(v)); 
                  if (formErrors.numero_licenca) setFormErrors(prev => ({ ...prev, numero_licenca: undefined })); 
                }} 
                keyboardType="number-pad" 
                mode="outlined" 
                error={!!formErrors.numero_licenca}
                placeholder="1234567890/2024"
                maxLength={15}
              />
              {formErrors.numero_licenca && (
                <Text style={{ color: theme.colors.error, marginTop: -8 }}>{formErrors.numero_licenca}</Text>
              )}
              {/* Render non-field errors if present */}
              {(formErrors.non_field_errors && formErrors.non_field_errors.length > 0) && (
                <View style={{ marginTop: 8 }}>
                  {formErrors.non_field_errors.map((msg, idx) => (
                    <Text key={`nfe-${idx}`} style={{ color: theme.colors.error }}>{msg}</Text>
                  ))}
                </View>
              )}
              {(formErrors.other && formErrors.other.length > 0) && (
                <View style={{ marginTop: 8 }}>
                  {formErrors.other.map((msg, idx) => (
                    <Text key={`other-${idx}`} style={{ color: theme.colors.error }}>{msg}</Text>
                  ))}
                </View>
              )}
              <Button mode="contained" onPress={handleSave} loading={saving} disabled={saving}>
                {t('save', 'Save')}
              </Button>
            </View>
          )}
        </ScrollView>
      </Surface>
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{ label: t('ok', 'OK'), onPress: () => setSnackbarVisible(false) }}
        wrapperStyle={isLargeScreen ? { left: 0, right: 0, bottom: 120, alignItems: 'center' } : undefined}
        style={isLargeScreen ? { maxWidth: 560, borderRadius: 8 } : undefined}
      >
        {snackbarMessage}
      </Snackbar>
      {!isLargeScreen && (
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={hasOwner}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasDriver && hasOwner ? (newProfile) => updateProfile(newProfile) : undefined}
          onRegisterDriver={hasOwner && !hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  content: {
    padding: 20,
  },
});


